/**
 * Growth Strategy Preview — homepage CTA component.
 *
 * Replaces the old Brand Kit teaser. Drop a URL, get a premium
 * personalised preview of the brand's next campaign with a
 * blurred sign-up CTA for the full strategy.
 *
 * Sections rendered:
 *   - Brand Snapshot
 *   - Brand Health Score (ring) + 4 mini indicators
 *   - Biggest Growth Gap
 *   - Industry Benchmark
 *   - Recommended Campaign (name + concept) + Why This Campaign
 *   - This Week's Plan (3-5 items — first 2 visible, rest blurred)
 *   - Visual Campaign Preview (blurred deck cover + moodboard + post mockup)
 *   - "Unlock My Full Strategy →" CTA
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { track } from './landing/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISP = "'Instrument Serif', Georgia, serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', monospace";
const LIME = '#c8f135';

const colorForScore = (n) => (n >= 70 ? '#c8f135' : n >= 45 ? '#f6c453' : '#ff9a76');

const MOOD_TILE_BG = [
  'linear-gradient(135deg, #c8f135, #6a8a30)',
  'linear-gradient(135deg, #1a1a1a, #2a2a2a)',
  'linear-gradient(135deg, #2c1810, #4a2b16)',
  'linear-gradient(135deg, #0f1a2e, #1e3354)',
  'linear-gradient(135deg, #2a0e2a, #4a1f4a)',
];

const Ring = ({ value, size = 96 }) => {
  const color = colorForScore(value || 0);
  const c = 2 * Math.PI * 16;
  const offset = c * (1 - (value || 0) / 100);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg viewBox="0 0 36 36" width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
        <circle cx="18" cy="18" r="16" fill="none" stroke={color} strokeWidth="2.5"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontFamily: DISP, fontSize: size * 0.32, color, fontWeight: 600, lineHeight: 1 }}>{value || 0}</span>
        <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.18em', color: 'rgba(245,240,232,0.45)', textTransform: 'uppercase', marginTop: 2 }}>health</span>
      </div>
    </div>
  );
};

const IndicatorBar = ({ label, score, note }) => {
  const color = colorForScore(score || 0);
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: 'rgba(245,240,232,0.7)', flex: 1 }}>{label}</span>
        <span style={{ fontFamily: DISP, fontSize: 14, color, fontWeight: 600 }}>{score || 0}</span>
      </div>
      <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score || 0}%`, background: color, borderRadius: 2 }} />
      </div>
      {note && <p style={{ fontFamily: MONO, fontSize: 9, color: 'rgba(245,240,232,0.42)', marginTop: 4, marginBottom: 0, lineHeight: 1.5 }}>{note}</p>}
    </div>
  );
};

export default function GrowthStrategyPreview({ defaultUrl = 'hooglam.com' }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState(defaultUrl);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const run = async (e) => {
    if (e) e.preventDefault();
    if (!url.trim()) return;
    setError('');
    setLoading(true);
    setPreview(null);
    try {
      const resp = await axios.post(`${API_URL}/api/launch-kit/strategy-preview`,
        { url: url.trim() },
        { timeout: 30000 }
      );
      const p = resp.data?.preview || null;
      setPreview(p);
      if (p && resp.data?.scan_token) {
        try {
          localStorage.setItem('scalitos_scan', JSON.stringify({
            token: resp.data.scan_token,
            url: url.trim(),
            host: resp.data.host || '',
            brand: p.brand || '',
            ts: Date.now(),
          }));
          sessionStorage.setItem('scl_launch_kit_url', url.trim());
        } catch (e) { /* storage unavailable */ }
      }
      track('scanner_completed', { host: resp.data?.host || url.trim() });
    } catch (err) {
      setError("We couldn't reach that site. Try a different URL.");
      track('scanner_failed', { url: url.trim() });
    } finally {
      setLoading(false);
    }
  };

  const goSignup = () => {
    track('scanner_unlock_clicked');
    navigate('/register');
  };

  return (
    <div data-testid="growth-strategy-preview" style={{ width: '100%', maxWidth: 1140, margin: '0 auto' }}>
      {/* URL INPUT */}
      <form onSubmit={run} data-testid="growth-preview-form"
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 10,
          background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16, padding: 10, maxWidth: 560, margin: '0 auto',
        }}>
        <input
          type="text" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="www.yourbrand.com" data-testid="growth-preview-url"
          style={{
            flex: 1, minWidth: 200, background: 'transparent', border: 'none', outline: 'none',
            color: '#f5f0e8', fontFamily: MONO, fontSize: 14, padding: '12px 14px',
          }}
        />
        <button type="submit" data-testid="growth-preview-submit" disabled={loading || !url.trim()}
          style={{
            background: LIME, color: '#0a0a0a', fontFamily: DISP, fontWeight: 600, fontSize: 14,
            border: 'none', padding: '12px 22px', borderRadius: 10,
            cursor: loading || !url.trim() ? 'not-allowed' : 'pointer',
            opacity: loading || !url.trim() ? 0.5 : 1,
            transition: 'opacity .2s', whiteSpace: 'nowrap',
          }}>
          {loading ? 'Analysing brand…' : 'Analyse My Brand →'}
        </button>
      </form>

      {error && (
        <p data-testid="growth-preview-error" style={{ textAlign: 'center', fontFamily: MONO, fontSize: 12, color: '#ff9a76', marginTop: 14 }}>{error}</p>
      )}

      {loading && (
        <div data-testid="growth-preview-loading" style={{ marginTop: 36, textAlign: 'center' }}>
          <p style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.22em', color: LIME, textTransform: 'uppercase' }}>Strategist working…</p>
          <p style={{ fontFamily: MONO, fontSize: 12, color: 'rgba(245,240,232,0.4)', marginTop: 10, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            Reading your site · checking the industry · finding your growth gap · writing your campaign
          </p>
        </div>
      )}

      {/* PREVIEW */}
      {preview && !loading && (
        <div data-testid="growth-preview-result" style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 18 }}>
          {/* Snapshot + Score */}
          <div style={{ padding: 22, background: '#101010', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 18, gridColumn: '1 / -1' }}>
            <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.22em', color: LIME, textTransform: 'uppercase', marginBottom: 12 }}>Brand snapshot</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
              <Ring value={preview.brand_health_score} size={110} />
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <p style={{ fontFamily: DISP, fontSize: 22, lineHeight: 1.35, color: '#f5f0e8', margin: 0, fontWeight: 500 }}>
                  {preview.brand_snapshot}
                </p>
                <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {preview.business_type && (
                    <span style={chipStyle}>{preview.business_type}</span>
                  )}
                  {preview.industry && (
                    <span style={chipStyle}>{preview.industry}</span>
                  )}
                  {preview.brand_stage && (
                    <span style={chipStyle}>{String(preview.brand_stage).replace(/_/g, ' ')}</span>
                  )}
                </div>
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                {(preview.mini_indicators || []).slice(0, 4).map((ind) => (
                  <IndicatorBar key={ind.label} {...ind} />
                ))}
              </div>
            </div>
          </div>

          {/* Growth gap */}
          <div style={{ ...cardStyle, borderLeft: '2px solid #ff9a76' }}>
            <p style={{ ...kickerStyle, color: '#ff9a76' }}>Biggest growth gap</p>
            <p style={{ fontFamily: DISP, fontSize: 19, lineHeight: 1.4, color: '#fff', margin: 0, fontWeight: 500 }}>
              {preview.growth_gap}
            </p>
          </div>

          {/* Benchmark */}
          <div style={cardStyle}>
            <p style={kickerStyle}>Industry benchmark</p>
            <p style={{ fontFamily: MONO, fontSize: 12, lineHeight: 1.7, color: 'rgba(245,240,232,0.78)', margin: 0 }}>
              {preview.industry_benchmark}
            </p>
          </div>

          {/* Campaign — main card */}
          <div style={{ ...cardStyle, borderLeft: '2px solid #c8f135', gridColumn: '1 / -1' }}>
            <p style={{ ...kickerStyle, color: LIME }}>Recommended campaign</p>
            <h3 style={{ fontFamily: DISP, fontSize: 36, lineHeight: 1.1, color: '#fff', margin: '4px 0 14px', fontWeight: 500, letterSpacing: '-0.01em' }}>
              {preview.campaign_name}.
            </h3>
            <p style={{ fontFamily: DISP, fontSize: 16, lineHeight: 1.55, color: 'rgba(245,240,232,0.85)', margin: '0 0 18px', fontStyle: 'italic' }}>
              {preview.campaign_concept}
            </p>
            {preview.why_this_campaign && (
              <div style={{ padding: '12px 16px', background: 'rgba(200,241,53,0.03)', borderRadius: 10, borderLeft: '1px solid rgba(200,241,53,0.2)' }}>
                <p style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: 'rgba(245,240,232,0.5)', textTransform: 'uppercase', marginBottom: 6 }}>Why this campaign</p>
                <p style={{ fontFamily: MONO, fontSize: 11, lineHeight: 1.7, color: 'rgba(245,240,232,0.75)', margin: 0 }}>
                  {preview.why_this_campaign}
                </p>
              </div>
            )}
          </div>

          {/* Week plan teaser + Visual mockup — 2 cols on wide */}
          <div style={{ ...cardStyle, position: 'relative', overflow: 'hidden' }}>
            <p style={kickerStyle}>This week's plan</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
              {(preview.week_plan_preview || []).map((d, i) => {
                const locked = i >= 2;
                return (
                  <div key={i} style={{
                    display: 'flex', gap: 12, alignItems: 'center', padding: '10px 12px',
                    background: locked ? 'rgba(255,255,255,0.02)' : 'rgba(200,241,53,0.04)',
                    border: '1px solid ' + (locked ? 'rgba(255,255,255,0.05)' : 'rgba(200,241,53,0.12)'),
                    borderRadius: 10, filter: locked ? 'blur(2.5px)' : 'none', opacity: locked ? 0.55 : 1,
                    transition: 'filter .3s',
                  }}>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: 'rgba(245,240,232,0.4)', width: 60 }}>{d.day}</span>
                    <span style={{ fontFamily: DISP, fontSize: 14, color: '#fff', flex: 1 }}>{d.title}</span>
                    <span style={{ fontFamily: MONO, fontSize: 9, color: 'rgba(245,240,232,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{d.type}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual preview tiles */}
          <div style={{ ...cardStyle, position: 'relative', overflow: 'hidden' }}>
            <p style={kickerStyle}>Visual campaign preview</p>
            <div style={{
              marginTop: 8, padding: '18px 16px', borderRadius: 12,
              background: 'linear-gradient(180deg, #0e0e0e, #060606)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <p style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.22em', color: LIME, textTransform: 'uppercase', marginBottom: 6 }}>Deck cover</p>
              <p style={{ fontFamily: DISP, fontSize: 22, lineHeight: 1.15, color: '#fff', margin: 0, fontWeight: 500 }}>
                {preview.visual_preview?.cover_headline || preview.campaign_name}.
              </p>
              <p style={{ fontFamily: DISP, fontSize: 13, fontStyle: 'italic', color: 'rgba(245,240,232,0.55)', margin: '6px 0 0' }}>
                {preview.visual_preview?.cover_subhead || ''}
              </p>
            </div>
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
              {(preview.visual_preview?.moodboard_keywords || ['Editorial', 'Warm', 'Bold', 'Quiet']).slice(0, 4).map((kw, i) => (
                <div key={i} style={{
                  height: 64, borderRadius: 8, padding: 8,
                  background: MOOD_TILE_BG[i % MOOD_TILE_BG.length],
                  color: i === 0 ? '#0a0a0a' : 'rgba(245,240,232,0.92)',
                  display: 'flex', alignItems: 'flex-end',
                  fontFamily: DISP, fontSize: 11, fontWeight: 500, lineHeight: 1.2,
                  filter: 'blur(0.5px)',
                }}>{kw}</div>
              ))}
            </div>
            {/* Blurred post mockup */}
            <div style={{
              marginTop: 10, padding: 12, borderRadius: 10,
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
              filter: 'blur(2.5px)', opacity: 0.6,
            }}>
              <div style={{ height: 8, width: '60%', background: 'rgba(245,240,232,0.18)', borderRadius: 3, marginBottom: 6 }} />
              <div style={{ height: 6, width: '92%', background: 'rgba(245,240,232,0.1)', borderRadius: 3, marginBottom: 4 }} />
              <div style={{ height: 6, width: '78%', background: 'rgba(245,240,232,0.1)', borderRadius: 3 }} />
            </div>
          </div>

          {/* UNLOCK CTA */}
          <div style={{ gridColumn: '1 / -1', marginTop: 8, textAlign: 'center' }}>
            <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.22em', color: LIME, textTransform: 'uppercase', marginBottom: 16 }}>Locked: full deck · 7-day plan · content mockups · PDF export</p>
            <div style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button onClick={goSignup} data-testid="growth-preview-unlock"
                style={{
                  background: LIME, color: '#0a0a0a', fontFamily: DISP, fontWeight: 600, fontSize: 16,
                  border: 'none', padding: '14px 30px', borderRadius: 999, cursor: 'pointer',
                }}>
                Unlock My Full Strategy →
              </button>
              <button onClick={() => navigate('/login')} data-testid="growth-preview-signin"
                style={{
                  background: 'transparent', color: 'rgba(245,240,232,0.65)',
                  fontFamily: MONO, fontSize: 12, border: '1px solid rgba(255,255,255,0.12)',
                  padding: '14px 22px', borderRadius: 999, cursor: 'pointer',
                }}>
                Sign in →
              </button>
            </div>
            <p style={{ fontFamily: MONO, fontSize: 10, color: 'rgba(245,240,232,0.35)', marginTop: 14 }}>
              Free preview. No card required. Unlock the deck in under 60 seconds.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = {
  padding: 22,
  background: '#101010',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 18,
};

const kickerStyle = {
  fontFamily: MONO,
  fontSize: 9,
  letterSpacing: '0.22em',
  color: LIME,
  textTransform: 'uppercase',
  marginBottom: 12,
  marginTop: 0,
  fontWeight: 700,
};

const chipStyle = {
  display: 'inline-block',
  padding: '4px 11px',
  borderRadius: 999,
  fontFamily: MONO,
  fontSize: 10,
  letterSpacing: '0.06em',
  color: 'rgba(245,240,232,0.7)',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  textTransform: 'capitalize',
};
